/**
 * src/components/ui/FileUpload.jsx — drag-and-drop reference-image picker.
 *
 * A real `<input type="file">` is always present and always operable by
 * keyboard/screen reader/mobile file picker — drag-and-drop is layered on
 * top as a convenience, never a replacement, per the same principle
 * REDESIGN.md's Dashboard section applies to drag-to-reschedule ("any
 * drag-and-drop interaction gets a non-drag equivalent"). The input is
 * visually hidden with a clip-path technique (not `display:none`, which
 * would also remove it from the tab order and from being clickable) and the
 * whole dropzone is its `<label>`, so clicking anywhere opens the OS file
 * picker without any JS click-forwarding.
 *
 * Presentational only — this phase builds the component, not its wiring
 * into a real page's upload flow (services/r2.js, the presign endpoints);
 * that's Phase 4/5's job when the pages that use it get redesigned.
 */
import { useId, useState } from 'react';

/**
 * @param {{
 *   label: string,
 *   hint?: React.ReactNode,
 *   accept?: string,
 *   multiple?: boolean,
 *   onFiles: (files: File[]) => void,
 * }} props
 * @returns {JSX.Element}
 */
export function FileUpload({ label, hint, accept = 'image/*', multiple = true, onFiles }) {
  const inputId = useId();
  const hintId = hint ? `${inputId}-hint` : undefined;
  const [dragging, setDragging] = useState(false);

  const handleFiles = (fileList) => {
    const files = Array.from(fileList ?? []);
    if (files.length) onFiles(files);
  };

  return (
    <div className="ui-field">
      <label htmlFor={inputId}>{label}</label>
      {hint ? (
        <p id={hintId} className="ui-field-hint">
          {hint}
        </p>
      ) : null}
      {/* The label IS the dropzone, so a click anywhere in it opens the
          native picker with no extra JS — drag handlers only add visual
          feedback and route dropped files to the same input. */}
      <label
        htmlFor={inputId}
        className={`ui-dropzone${dragging ? ' ui-dropzone--active' : ''}`}
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <p className="ui-dropzone-text">
          <strong>Choose files</strong> or drag them here
        </p>
        <input
          type="file"
          id={inputId}
          className="ui-visually-hidden"
          accept={accept}
          multiple={multiple}
          aria-describedby={hintId}
          onChange={(e) => handleFiles(e.target.files)}
        />
      </label>
    </div>
  );
}
